import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  Modal,
  Heading,
  Button,
  Input,
  Flex,
  Box,
  Text,
  Divider
} from "samba-ui";
import VisibleOptionSetter from "./VisibleOptionSetter";
import useVisibleOptionsValues from "./hooks/useVisibleOptionsValues";

import SetDateRange from "./SetDateRange";

const StyledButton = styled(Button)`
  float: right;
`;

const StyledInput = styled(Input)`
  width: 300px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const PermissionModal = ({
  isOpen,
  onClose,
  onSubmit,
  permissionName,
  permissionFilterName,
  isVisible,
  isDefault,
  permissionFilterValues,
  isCustom
}) => {
  const [custom, setCustom] = useState(isCustom || false);
  const [name, setName] = useState(permissionName || "");
  const [filterName, setFilterName] = useState(permissionFilterName || "");
  const [visible, setVisible] = useState(isVisible || false);
  const [_isDefault, setIsDefault] = useState(isDefault || false);

  const [selectedOption, setSelectedOption] = useState({
    label: "All Dates",
    value: null
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [
    filterValues,
    setFilterValues,
    removeFilterValues
  ] = useVisibleOptionsValues(permissionFilterValues || []);

  const dateRangeOutput = () => {
    let output = null;

    if (selectedOption && selectedOption.value !== "custom") {
      output = selectedOption.value;
    } else if (selectedOption && selectedOption.value === "custom") {
      const start = startDate ? startDate.format("YYYY-MM-DD") : "-";
      const end = endDate ? endDate.format("YYYY-MM-DD") : "-";
      output = { start, end };
    }
    return output;
  };

  const onCreateClick = () => {
    const daterange = dateRangeOutput();
    const filters = {
      [filterName]: custom ? daterange : filterValues
    };

    const body = {
      name: name.trim(),
      filters,
      visible: visible ? [filterName] : [],
      isDefault: custom || _isDefault,
      isCustom: custom
    };

    onSubmit(body);
    clearState();
  };

  const isDisabled = !name || !filterName;

  const onChooseDate = (type, date) => {
    type === "start_date" ? setStartDate(date) : setEndDate(date);
  };

  const clearState = () => {
    // clear the state
    setCustom(false);
    setName("");
    setFilterName("");
    setVisible(false);
    setIsDefault(false);
  };

  const onRequestClose = () => {
    clearState();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} width={800}>
      <Modal.Header borderColor="#d6d6d6 !important">
        <Heading fontSize={2} fontWeight={500} color="secondary">
          Add new permission
        </Heading>
      </Modal.Header>
      <Modal.Content>
        <Flex mb={4} alignItems="center" justifyContent="space-between">
          <Text>Permission Name</Text>
          <StyledInput
            placeholder="Permission name"
            onChange={e => setName(e.target.value)}
            value={name}
          />
        </Flex>
        <Box mb={3}>
          <Divider />
        </Box>
        <Flex mb={4} alignItems="center" justifyContent="space-between">
          <Text>Is Date Range Filter</Text>
          <Checkbox
            type="checkbox"
            checked={custom}
            onChange={e => {
              setCustom(e.target.checked);
              if (e.target.checked) {
                setVisible(true);
                setIsDefault(true);
              }
            }}
          />
        </Flex>
        <Flex mb={4} alignItems="center" justifyContent="space-between">
          <Text>Filter Name</Text>
          <StyledInput
            placeholder="Filter name"
            onChange={e => setFilterName(e.target.value.trim())}
            value={filterName}
          />
        </Flex>
        {custom ? (
          <SetDateRange
            onChooseDate={onChooseDate}
            startDate={startDate}
            endDate={endDate}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        ) : (
          <>
            <Flex mt={4} alignItems="center" justifyContent="space-between">
              <Box>
                <Heading fontSize={1}>Filter Values</Heading>
                <Text fontSize={0} color="palette.ink.400">
                  Add filter values
                </Text>
              </Box>
              <Box mb={2} width={300}>
                <VisibleOptionSetter
                  tags={filterValues}
                  setTag={setFilterValues}
                  removeTag={removeFilterValues}
                />
              </Box>
            </Flex>
          </>
        )}
        <Flex mt={4} mb={3} alignItems="center" justifyContent="space-between">
          <Text>Visible</Text>
          <Checkbox
            type="checkbox"
            checked={visible}
            onChange={e => setVisible(e.target.checked)}
          />
        </Flex>
        <Flex mt={4} mb={3} alignItems="center" justifyContent="space-between">
          <Text>Default</Text>
          <Checkbox
            type="checkbox"
            checked={_isDefault}
            onChange={e => setIsDefault(e.target.checked)}
          />
        </Flex>
      </Modal.Content>
      <Modal.Actions justifyContent="flex-end" borderColor="#d6d6d6 !important">
        <Flex>
          <Box mr={3}>
            <Button variant="secondary" onClick={onRequestClose}>
              Cancel
            </Button>
          </Box>
          <StyledButton
            disabled={isDisabled}
            variant="primary"
            onClick={onCreateClick}
          >
            {permissionName ? "Update" : "Create"}
          </StyledButton>
        </Flex>
      </Modal.Actions>
    </Modal>
  );
};

PermissionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  permissionName: PropTypes.string,
  permissionFilterName: PropTypes.string,
  isVisible: PropTypes.bool,
  permissionFilterValues: PropTypes.array
};

export default PermissionModal;
